/**
 * Runtime theming and dark mode.
 *
 * The organisation record carries a `theme` object; applying it writes the
 * brand custom properties onto the document root. Because every component reads
 * its colours from those properties (see index.css), a tenant's palette takes
 * effect immediately after an administrator saves it.
 */

export interface TenantTheme {
  brand?: string;
  brandHover?: string;
  brandTint?: string;
  accent?: string;
  radius?: string;
}

const VARIABLE_MAP: Record<keyof TenantTheme, string> = {
  brand: "--brand-blue",
  brandHover: "--brand-blue-hover",
  brandTint: "--brand-blue-tint",
  accent: "--brand-gold",
  radius: "--radius",
};

export function applyTenantTheme(theme: TenantTheme | null | undefined): void {
  if (!theme) return;
  const root = document.documentElement;
  for (const [key, variable] of Object.entries(VARIABLE_MAP)) {
    const value = theme[key as keyof TenantTheme];
    if (value) root.style.setProperty(variable, value);
  }
}

const DARK_KEY = "lims.appearance";
export type Appearance = "light" | "dark" | "system";

export function resolveAppearance(): Appearance {
  const stored = window.localStorage.getItem(DARK_KEY);
  if (stored === "light" || stored === "dark" || stored === "system") return stored;
  return "system";
}

export function applyAppearance(appearance: Appearance): void {
  window.localStorage.setItem(DARK_KEY, appearance);
  const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  const dark = appearance === "dark" || (appearance === "system" && prefersDark);
  document.documentElement.classList.toggle("dark", dark);
  document.documentElement.style.colorScheme = dark ? "dark" : "light";
}
